import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import {
    BrowserRouter as Router,
    Route,
    Switch,
    Redirect,
} from 'react-router-dom';
import { decodeJwt } from "jose";
import qs from 'qs';
import { setCurrentUser } from '../redux/user/user-actions';
import { login } from '../redux/auth/auth-actions';
import Login from '../components/Login';
import ForgotPassword from '../components/ForgotPassword';
import SetNewPassword from '../components/SetNewPassword';
import NavigationBar from '../components/navigation-bar/navigation-bar.jsx';
import ErrorBoundary from '../components/error-pages/errorBoundary';
import GetStarted from '../components/GetStarted/GetStarted';
import TasksPage from '../pages/tasks/scenes/tasks-page/tasks-page.jsx';
import ProjectsPageNew from '../pages/projects/scenes/projects-page/projects-page.jsx';
import AdminPage from '../pages/admin/scenes/admin-page/admin-page';
import SettingsPage from '../pages/settings/scenes/settings-page/settings-page';
import ProfilePage from '../pages/profile/scenes/profile-page/profile-page';
import MissingPage from '../pages/errors/scenes/MissingPage/missing-page';
import ForbiddenPage from '../pages/errors/scenes/ForbiddenPage/forbidden-page';
import ServerPage from '../pages/errors/scenes/ServerErrorPage/server-page';
import SearchDetailedPage from '../pages/search-detailed/SearchDetailedPage';
import NewDashboard from '../pages/new-dashboard/new-dashboard';
import NotificationPageNew from '../pages/notifications/notifications/notification-page/notification-page-new.jsx';
import Onboarding from '../pages/Onboarding';
import ExternalForm from '../pages/external-form/external-form.jsx';
import {
    GetCurrentUser,
    GetCurrentUserNewAPI,
    AzureUserLogin,
    GetGraphTokenByToken,
    LoginContext
} from '../services/api/authentication-api';
import { GetDepartment } from '../services/api/department-api';
import { GetRolePrivilege } from '../services/api/role-privilege-api';
import { GetProfile } from '../services/api/profile-api';
import { getUserSettings } from '../services/api/settings-api.js';
import { GetDefaultLandingPage, GetDefaultObjectLabels } from '../services/api/subdepartment-api.js';
import { getToken, setAuthCookie, removeAuthCookie } from '../services/api/api-util.js';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig, loginRequest } from '../authConfigAzureAd.js';
import '@progress/kendo-theme-default/dist/all.css';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/globals.scss';

const msalInstance = new PublicClientApplication(msalConfig);
const authType = window["APP_SETTINGS"].AUTH_TYPE;

const AppRender = () => {
    const dispatch = useDispatch();
    const context = useContext(LoginContext);
    const [apiToken, setApiToken] = useState(null);
    const [initDone, setInitDone] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserAPI, setCurrentUserAPI] = useState(null);
    const [currentUserContext, setCurrentUserContext] = useState(null);
    const [department, setDepartment] = useState(null);
    const [rolePrivileges, setRolePrivileges] = useState(null);
    const [profile, setProfile] = useState(null);
    const [userReady, setUserReady] = useState(false);
    const [loggedIn, setLoggedIn] = useState(false);
    const [showGetStarted, setShowGetStarted] = useState(false);
    const [organizationName, setOrganizationName] = useState('');
    const [landingPage, setLandingPage] = useState('tasks');
    const [usingNewSite] = useState(window.APP_SETTINGS.REACT_APP_USING_NEW_UI === 'TRUE');
    const timezone = new Date().getTimezoneOffset();

    const initializeApp = useCallback(async (tokenFromLogin) => {
        console.log('AppRender.js [initializeApp] called with token:');
        console.log(tokenFromLogin);

        if (!tokenFromLogin) {
            console.log('AppRender.js [initializeApp]: No token provided');
            return;
        }

        if (!tokenFromLogin.resultToken || tokenFromLogin.resultToken == "") {
            console.log('AppRender.js [initializeApp]: Invalid token');
            return;
        }

        if (authType === "AzureAD") {
            console.log("AppRender.js Auth Type AzureAD");

            let graphToken = tokenFromLogin.graphToken;
            if (!graphToken || graphToken == "") {
                console.log("GetGraphTokenByToken =>");
                try {
                    let data = {
                        token: tokenFromLogin.resultToken,
                        graphToken: "",
                    };
                    graphToken = await GetGraphTokenByToken(data);
                    console.log(graphToken);
                } catch (err) {
                    console.log('GetGraphTokenByToken:', err);
                    authAzureAd();
                    return;
                }
            }

            if (!graphToken || graphToken == "") {
                console.log("AppRender.js [No Cookie Value] --> calling authAzureAd...");
                authAzureAd();
                return;
            } else {
                console.log("AppRender.js Cookie Value -->");
                const decodedToken = JSON.parse(atob(graphToken.split(".")[1]));
                const currentTime = Date.now() / 1000;
                if (decodedToken.exp > currentTime) {
                    console.log("AppRender.js Graph Token Not Expired -->");

                    // This is just to check the token is working for graph endpoints. Will be removed later. Keep it for now.
                    const graphResponse = await fetch("https://graph.microsoft.com/v1.0/me", {
                        headers: {
                            Authorization: `Bearer ${graphToken}`
                        }
                    });
                    const profileData = await graphResponse.json();
                    console.log("AppRender.js Graph Profile Data -->");
                    console.log(profileData);
                } else {
                    console.log("AppRender.js [Graph Token Expired] --> calling authAzureAd...");
                    authAzureAd();
                    return;
                }
            }
        }

        setApiToken(tokenFromLogin.Token);
        setInitDone(true);

        let profRes;
        try {
            profRes = await GetProfile();
            console.log(profRes);
            if (profRes) {
                setProfile(profRes);
            }
        } catch (err) {
            console.log('GetProfile:', err);
        }

        let currentUserIdRes;
        try {
            currentUserIdRes = await GetCurrentUser();
            console.log(currentUserIdRes);
            if (currentUserIdRes) {
                setCurrentUserId(currentUserIdRes);
            }
        } catch (err) {
            console.log('GetCurrentUser:', err);
        }

        let currentUserAPIRes;
        try {
            currentUserAPIRes = await GetCurrentUserNewAPI();
            console.log(currentUserAPIRes);
            if (currentUserAPIRes) {
                setCurrentUserAPI(currentUserAPIRes);
            }
        } catch (err) {
            console.log('GetCurrentUserNewAPI:', err);
        }

        let userPassword = profRes.password;
        if (profRes.isSSO && !(userPassword?.length > 0)) {
            // Random password just to pass the LoginDto object validation that enforce the password to be at least 1 character long
            userPassword = "SBu2gr1Sqtne0iEHKcLdMp5YxiW5sxo0";
        }

        dispatch(login({
            Username: profRes.userName,
            Password: userPassword,
            Timezone: timezone,
        }));

        try {
            const [deptRes, roleRes, landingRes, userSetRes, objectLabelsRes] = await Promise.all([
                GetDepartment(currentUserAPIRes.User.CompanyId),
                GetRolePrivilege(currentUserAPIRes.User.RoleId),
                GetDefaultLandingPage(),
                getUserSettings(),
                GetDefaultObjectLabels(),
            ]);

            console.log("GetDepartment=>");
            console.log(deptRes);
            setDepartment(deptRes);

            console.log("GetRolePrivilege=>");
            console.log(roleRes);

            let privileges = {};
            roleRes.forEach((privilege) => {
                privileges[privilege.MenuItem] = privilege;
            });
            setRolePrivileges(privileges);

            setOrganizationName(deptRes.Name);

            console.log("GetDefaultLandingPage=>");
            console.log(landingRes.data)
            setLandingPage(landingRes.data);

            console.log("getUserSettings=>");
            console.log(userSetRes)

            const user = { profile: profRes, department: deptRes, privileges: privileges, token: currentUserAPIRes.Token, objectLabels: objectLabelsRes.data };

            console.log("const user =");
            console.log(user);

            setCurrentUserContext(user);
            dispatch(setCurrentUser(user));

            setUserReady(true);
            setLoggedIn(true);
        } catch (err) {
            console.error('AppRender.js [initializeApp] fetch chain error:', err);
        }
    }, [dispatch]);

    const authAzureAd = async () => {
        console.log("AppRender.js Aquiring access token -->");
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        console.log("AppRender.js accounts", accounts);
        if (accounts.length > 0) {
            console.log("AppRender.js accounts.length > 0");
            const request = {
                ...loginRequest,
                account: accounts[0],
            };

            console.log("AppRender.js authAzureAd -->")
            await msalInstance
                .acquireTokenSilent(request)
                .then(async (response) => {
                    console.log(response);
                    console.log("AppRender.js authAzureAd ID Token:", response.idToken);
                    console.log("AppRender.js authAzureAd Access Token:", response.accessToken);

                    let accessToken = response.accessToken || "";
                    let userJWT = decodeJwt(accessToken);
                    let username = response.account?.username || userJWT.preferred_username || userJWT.upn || userJWT.unique_name || "";
                    let tenantId = response.tenantId || userJWT.tid || "";
                    let graphConnectorId = response.idTokenClaims?.aud || userJWT.aud || userJWT.appid || "";

                    let data = {
                        username: username,
                        tenantId: tenantId,
                        graphConnectorId: graphConnectorId,
                        graphToken: accessToken,
                        timezone: timezone,
                    };

                    await AzureUserLogin(data).then(
                        (result) => {
                            if (result.Token != null) {
                                let resultToken = result.Token ?? "";
                                let graphToken = result.GraphToken ?? "";
                                console.log("AppRender.js [AzureUserLogin] result -->");
                                console.log(result);

                                console.log("AppRender.js [AzureUserLogin] setAuthCookie -->");
                                setAuthCookie(resultToken);
                                initializeApp({ resultToken, graphToken });
                            } else {
                                throw new Error("[AzureUserLogin]: Token was not received");
                            }
                        },
                        (error) => {
                            console.log("AppRender.js AzureUserLogin [error]: ");
                            console.log(error);
                        }
                    );

                })
                .catch((error) => {
                    if (error.errorMessage.includes("interaction_required")) {
                        msalInstance.acquireTokenRedirect(request);
                    } else {
                        console.log("AppRender.js authAzureAd Access token error -->");
                        console.error(error);
                    }
                });
        }
    };

    useEffect(() => {
        const resultToken = getToken();
        const graphToken = "";
        if (resultToken) initializeApp({ resultToken, graphToken });
    }, [initializeApp]);

    const contextValue = useMemo(() => ({
        hostURL: '',
        userID: profile?.userID || currentUserId,
        loggedIn,
        usingNewSite,
        currentUser: currentUserContext
    }), [profile, loggedIn, usingNewSite, currentUserContext]);

    const setLoggedOut = useCallback(() => {
        removeAuthCookie();
        setLoggedIn(false);
        window.location.href = '/login';
    }, []);

    const routeConfig = useMemo(() => [
        { path: '/dashboard', component: NewDashboard },
        { path: '/projects', component: ProjectsPageNew },
        { path: '/tasks', component: TasksPage },
        { path: '/admin', component: AdminPage },
        { path: '/settings', component: SettingsPage },
        { path: '/profile', component: ProfilePage },
        { path: '/notifications', component: NotificationPageNew },
        { path: '/searchresults/:type', component: SearchDetailedPage },
        { path: '/', exact: true, component: () => <Redirect to={`/${landingPage}`} /> },
        { path: '/missing', component: MissingPage },
        { path: '/forbidden', component: ForbiddenPage },
        { path: '/internal-error', component: ServerPage },
        { path: '/external-form/:formId', component: ExternalForm },
    ], [landingPage]);

    return (
        <MsalProvider instance={msalInstance}>
            <LoginContext.Provider value={contextValue}>
                <div className="App">
                    <Router>
                        <Route render={() => (
                            loggedIn ? (
                                <React.Fragment>
                                    <GetStarted
                                        show={showGetStarted}
                                        onHide={() => setShowGetStarted(false)}
                                        organizationName={organizationName}
                                    />
                                    <NavigationBar key={currentUserId} onLogout={setLoggedOut} />
                                    <div className='navbar-margin'>
                                        <ErrorBoundary>
                                            <TransitionGroup>
                                                <CSSTransition
                                                    key={`${window.location.pathname ?? "location-pathname"}`}
                                                    timeout={300}
                                                    appear={true}
                                                    classNames="section"
                                                >
                                                    <Switch>
                                                        {routeConfig.map((route, i) => (
                                                            <Route
                                                                key={`${route.path ?? "route-path"}-${i}`}
                                                                path={route.path}
                                                                exact={route.exact}
                                                                render={props => <div className="section"><route.component {...props} /></div>}
                                                            />
                                                        ))}
                                                    </Switch>
                                                </CSSTransition>
                                            </TransitionGroup>
                                        </ErrorBoundary>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <Switch key={"switch"}>
                                    <Route exact path={["/", "/login"]}>
                                        <Login onLogin={initializeApp} />
                                    </Route>
                                    <Route path="/registrations">
                                        <Onboarding onLogin={initializeApp} onHide={() => setShowGetStarted(false)} />
                                    </Route>
                                    <Route path="/forgot-password" component={ForgotPassword} />
                                    <Route path="/reset-password" component={SetNewPassword} />
                                </Switch>
                            )
                        )} />
                    </Router>
                </div>
            </LoginContext.Provider>
        </MsalProvider>
    );
};

export default AppRender;
